import {
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { CmpsPopulationCompareResult } from '@cmps/ng-kit';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { animationFrameScheduler, of } from 'rxjs';
import {
    auditTime,
    catchError,
    delay,
    retryWhen,
    scan,
    take,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { SystemComponent } from '../insight/config.insight.model';
import { PerformanceDataViewType } from '../performance-data/metric.pd.model';
import { StateToProps } from '../store';
import { useDispatcher, UseStore } from '../store.decorator';

import { CardType } from './models/card-type.model';
import { CustomPerformanceDataViewType } from './models/customer-performance-data-view-type.enum';
import { DropdownMenuValues } from './models/drop-down-menu-values.enum';
import { MetricItem } from './models/metric-item.model';
import {
    clearState,
    reload,
    selectMenuItem,
    selectPeriod,
    selectPopulation,
} from './my-performance-card.action';
import { TranslationKeyMap } from './my-performance-card.i18n';
import { performanceCardReducer } from './my-performance-card.reducer';
import { PerformanceCardState } from './my-performance-card.state';
import { PeriodCompareDialogResult } from './period-compare-dialog/period-compare-dialog-result.model';
import { CmpsPeriodCompareDialogComponent } from './period-compare-dialog/period-compare-dialog.component';
import { CmpsPopulationCompareDialogComponent } from './population-compare-dialog/population-compare-dialog.component';
@Component({
    selector: '[cmpsMyPerformanceCard]',
    templateUrl: './my-performance-card.component.html',
})
@TakeUntilDestroy()
@UseStore<PerformanceCardState>('performanceCard', performanceCardReducer)
export class MyPerformanceCardComponent
    implements OnInit, OnDestroy, StateToProps<PerformanceCardState>
{
    state: PerformanceCardState;

    /** @private */ _i18n = TranslationKeyMap;

    /** @private */ _isExpanded: boolean;
    /** @private */ _performanceDataViewType = PerformanceDataViewType;
    /** @private */ _dialogRef: CmpsDialogRef<
        CmpsPeriodCompareDialogComponent,
        PeriodCompareDialogResult
    >;

    /** @private */ _dialogRefPopulation: CmpsDialogRef<
        CmpsPopulationCompareDialogComponent,
        any
    >;

    private _modalRefs: {
        populationCompare?: CmpsDialogRef<
            CmpsPopulationCompareDialogComponent,
            CmpsPopulationCompareResult
        >;
        periodCompare?: CmpsDialogRef<
            CmpsPeriodCompareDialogComponent,
            PeriodCompareDialogResult
        >;
    };

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef<HTMLElement>,
    ) {
        this._modalRefs = {};
        this._isExpanded = false;
    }

    ngOnInit(): void {
        this._onReload();
    }

    ngOnDestroy(): void {
        useDispatcher(this, clearState({}));
    }

    /** @private */
    _onExpandClick(): void {
        this._isExpanded = !this._isExpanded;
        const cardElement = document.getElementsByClassName(
            'my-performance-card',
        )[0].parentElement.classList;
        cardElement.toggle('insight-component-item--expanded');

        this._scrollIntoView('.my-performance-card');
    }

    /** @private */
    _onSelectItem(event: any, pillarIndex: number): void {
        const value = event.id;
        if (
            value === DropdownMenuValues.PERIOD_COMPARE &&
            event.subMenu != null
        ) {
            let cardType: string;
            switch (this.state.performanceCard.component) {
                case SystemComponent.HUMAN_INSIGHT:
                    cardType = CardType.HUMAN_INSIGHT;
                    break;
                case SystemComponent.PERFORMANCE_INSIGHT:
                    cardType = CardType.PERFORMANCE_INSIGHT;
                    break;
                default:
                    cardType = CardType.DEFAULT;
                    break;
            }
            const ref = (this._modalRefs.periodCompare = this._dialog.open(
                CmpsPeriodCompareDialogComponent,
                {
                    disableClose: true,
                    data: this.state.periodCompareDialogData,
                },
            ));
            ref.result
                .pipe(
                    tap((ret) => {
                        if (ret?.selected != null) {
                            useDispatcher(
                                this,
                                selectPeriod({
                                    user: this._authService.user,
                                    selectedDate: ret.selected,
                                    pillarIndex,
                                    cardType,
                                }),
                            );
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        } else if (
            value === DropdownMenuValues.LAUNCH_RADIAL &&
            event.subMenu != null
        ) {
            this._onExpandClick();
        } else if (
            (value === DropdownMenuValues.POPULATION_COMPARE &&
                this.state.performanceCard?.component !==
                    SystemComponent.HUMAN_INSIGHT) ||
            value === CustomPerformanceDataViewType.POPULATION_VIEW
        ) {
            let metrics: MetricItem[] = [];
            if (pillarIndex === undefined) {
                metrics =
                    this.state.totalPerformancePillar.parentPillar.metrics;
            } else {
                metrics =
                    this.state.pillarsData[pillarIndex].parentPillar.metrics;
            }
            const metricsIds = metrics?.map((ee) => {
                return ee.metricID;
            });
            const ref = (this._modalRefs.populationCompare = this._dialog.open(
                CmpsPopulationCompareDialogComponent,
                {
                    disableClose: true,
                    data: this.state.populationCompareDialogData,
                },
            ));
            ref.result
                .pipe(
                    tap((ret) => {
                        if (ret != null) {
                            useDispatcher(
                                this,
                                selectPopulation({
                                    user: this._authService.user,
                                    selectedData: ret,
                                    pillarIndex,
                                    metricsIds,
                                }),
                            );
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        } else {
            useDispatcher(
                this,
                selectMenuItem({
                    index: pillarIndex,
                    menuItemId: value,
                }),
            );
        }
    }

    /** @private */
    _onReload(): void {
        useDispatcher(
            this,
            reload({
                user: this._authService.user,
            }),
        );
    }

    /** @private */
    _closeAction(viewType: PerformanceDataViewType, pillarIndex: number): void {
        useDispatcher(
            this,
            selectMenuItem({
                index: pillarIndex,
                menuItemId: viewType,
            }),
        );
    }

    private _scrollIntoView(selector: string): void {
        this._ngZone.onStable
            .pipe(
                take(1),
                delay(1000),
                tap(() => {
                    const el = this._el.nativeElement.querySelector(
                        selector,
                    ) as HTMLElement;
                    if (el != null) {
                        throw new Error('not found');
                    }
                    this._scrollIn(el);
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        scan((acc) => {
                            if (acc >= 5) {
                                throw new Error('still not found');
                            }
                            return acc + 1;
                        }, 0),
                        auditTime(0, animationFrameScheduler),
                    ),
                ),
                catchError(() => {
                    return of(null);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _scrollIn(element: HTMLElement): void {
        const rect = element.parentElement.getBoundingClientRect();
        const scrollTop =
            window.pageYOffset != null
                ? window.pageYOffset
                : document.documentElement.scrollTop;
        const offset = {
            top: rect.top + scrollTop,
        };

        const navbarHeight = document
            .querySelector('cmps-nav>.navbar.fixed-top')
            .getBoundingClientRect().height;
        const top = offset.top - navbarHeight - 17;
        try {
            window.scrollTo({
                left: 0,
                top,
                behavior: 'smooth',
            });
        } catch (e) {
            window.scrollTo(0, top);
        }
    }
}
